<script setup lang="ts">
import MeterGroup, { type MeterItem } from 'primevue/metergroup';
import Button from 'primevue/button';
import { computed, } from 'vue';
import { Unit } from 'shared/common/domain/bulkUnits';
import { useTranslations } from 'shared/translations/composables/useTranslations';
import { formatNumber } from 'shared/common/domain/helperFunctions'
import { toDisplayWeight } from 'shared/common/domain/weightUnits';

const { products } = defineProps<{
  products: BulkProductContentModel[];
}>();

const emit = defineEmits<{
  click: [content: BulkProductContentModel]
}>();

const translations = useTranslations();


const meterColors = [
  'var(--p-blue-400)',
  'var(--p-emerald-400)',
  'var(--p-yellow-400)',
  'var(--p-sky-400)',
  'var(--p-purple-400)',
  'var(--p-orange-400)',
  'var(--p-pink-400)',
];

const meterItems = computed(() => {
  const items: MeterItem[] = [];

  let index = 0;

  for (const content of products) {
    const item: MeterItem = {
      label: content.productName,
      value: content.ton,
      color: getColor(index),
    };
    items.push(item);
    index++;
  }

  items.sort((a, b) =>
    a.label.localeCompare(b.label, undefined, {
      numeric: true,
    }),
  );

  return items;
});

const totalValue = computed(() => {
  let count = 0;

  for (const product of products) {
    count += product.ton;
  }

  return count;
});

const meterVisible = computed(() => totalValue.value > 0 && products.length > 1);

export interface BulkProductContentModel {
  productId: string;
  productName: string;
  ton: number;
  displayUnit: Unit;
  displayValue: number;
}

function getColor(index: number) {
  index = index % meterColors.length;
  return meterColors[index];
}
</script>

<template>
  <div class="root">
    <MeterGroup v-if="meterVisible" :value="meterItems" :max="totalValue"></MeterGroup>
    <div class="content-list">
      <Button v-for="content in products" :key="content.productId" severity="secondary" @click="emit('click', content)"
        class="content-item">
        <span class="product-title">{{ content.productName }}</span>
        <span class="product-count">{{ formatNumber(content.displayValue) }} {{
          translations.bulk.bulkUnitShort(content.displayUnit) }}</span>
      </Button>
    </div>
  </div>
</template>

<style scoped>
.root {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.content-list {
  display: grid;
  grid-template-columns: 1fr;
  row-gap: .5rem;
}

.content-item {
  display: grid;
  grid-template-columns: 1fr auto;
}

.product-title {
  justify-self: start;
}
</style>
